import { useEffect, useMemo, useState } from 'react'
import { Box, Link, Typography } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import moment, { Moment } from 'moment'
import adjustmentApi from 'src/api/adjustmentApi'
import { ExternalLinkIcon } from 'src/components/Icons'

type ParticipantUser = {
    id: number
    user_name?: string
    email?: string
}

type GaroonParticipantsScheduleLinkProps = {
    participants: ParticipantUser[]
    rangeStart: Moment | null
    adjustmentStartDate?: Dayjs | string | Date | null
}

const getParticipantGaroonCode = (participant: ParticipantUser): string | undefined => {
    return participant.user_name || participant.email
}

const rowContainerSx = {
    width: '100%',
    textAlign: 'left',
    minHeight: '28px',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
} as const

const GaroonParticipantsScheduleLink = ({
    participants,
    rangeStart,
    adjustmentStartDate,
}: GaroonParticipantsScheduleLinkProps) => {
    const [garoonLink, setGaroonLink] = useState<string>('')
    const [isUpdatingLink, setIsUpdatingLink] = useState(false)

    const participantsWithGaroonCode = useMemo(
        () =>
            participants
                .map((participant) => ({
                    participant,
                    code: getParticipantGaroonCode(participant),
                }))
                .filter((entry): entry is { participant: ParticipantUser; code: string } => !!entry.code),
        [participants],
    )

    const participantKey = useMemo(
        () => participantsWithGaroonCode.map((entry) => entry.participant.id).join(','),
        [participantsWithGaroonCode],
    )

    const effectiveRangeStart = useMemo(() => {
        if (rangeStart) {
            return rangeStart.clone().startOf('day')
        }
        if (adjustmentStartDate) {
            return moment(dayjs(adjustmentStartDate).startOf('day').toDate())
        }
        return moment().startOf('day')
    }, [rangeStart, adjustmentStartDate])

    const rangeStartKey = effectiveRangeStart.format('YYYY-MM-DD')

    useEffect(() => {
        if (!participantsWithGaroonCode.length) {
            setGaroonLink('')
            setIsUpdatingLink(false)
            return
        }

        let cancelled = false
        setIsUpdatingLink(true)

        const fetchLink = async () => {
            try {
                const res: any = await adjustmentApi.getGaroonViewLink({
                    attendees: participantsWithGaroonCode.map(({ code }) => ({
                        type: 'USER',
                        code,
                    })),
                    rangeStart: effectiveRangeStart.toISOString(),
                })

                if (!cancelled) {
                    setGaroonLink(res?.data?.garoon_view_link ?? '')
                }
            } catch {
                if (!cancelled) {
                    setGaroonLink('')
                }
            } finally {
                if (!cancelled) {
                    setIsUpdatingLink(false)
                }
            }
        }

        fetchLink()

        return () => {
            cancelled = true
        }
    }, [participantKey, rangeStartKey, participantsWithGaroonCode, effectiveRangeStart])

    if (!participantsWithGaroonCode.length) {
        return null
    }

    const showLink = !!garoonLink
    const showUpdatingMessage = isUpdatingLink

    if (!showUpdatingMessage && !showLink) {
        return null
    }

    return (
        <Box sx={rowContainerSx}>
            {showLink && (
                <Link
                    underline="none"
                    color="#0075FF"
                    sx={{
                        fontSize: '15px',
                        lineHeight: '20px',
                        fontWeight: '400',
                        cursor: isUpdatingLink ? 'default' : 'pointer',
                        pointerEvents: isUpdatingLink ? 'none' : 'auto',
                        visibility: isUpdatingLink ? 'hidden' : 'visible',
                        display: 'inline-flex',
                        alignItems: 'center',
                    }}
                    href={garoonLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-hidden={isUpdatingLink}
                    tabIndex={isUpdatingLink ? -1 : 0}
                >
                    <Box component="span" sx={{ borderBottom: '1px solid #0075FF' }}>
                        Garoonで参加者の予定を見る
                    </Box>
                    <Box
                        component="span"
                        sx={{
                            verticalAlign: 'bottom',
                            paddingLeft: '2px',
                            position: 'relative',
                            top: '1px',
                            display: 'inline-flex',
                        }}
                    >
                        <ExternalLinkIcon />
                    </Box>
                </Link>
            )}

            {showUpdatingMessage && (
                <Typography
                    sx={{
                        fontSize: '15px',
                        lineHeight: '20px',
                        fontWeight: '400',
                        color: '#666666',
                        ...(showLink
                            ? {
                                  position: 'absolute',
                                  left: 0,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                              }
                            : {}),
                    }}
                >
                    Garoonリンクを更新中...
                </Typography>
            )}
        </Box>
    )
}

export default GaroonParticipantsScheduleLink
